import * as React from 'react';
import { useUpdateEffect } from 'react-use';
import { Slider } from '@sunwebgroup/ui-components';
import { useValueFilter } from '../../../../../domain/hooks';
import { useIntl } from '@/intl';
import FilterGroup from '../FilterGroup';
import { RangeLabel } from './RangeLabel';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createSearchFilterClickEvent } from '../../../../../domain/analytics';
import { pricePerPersonFilterRangeValues } from '@/scopes/search/search/domain/policies/currencies';

export function useControlledSliderWithOpenRange(
  selectedValue: [number, number | null] | undefined,
  setSelection: (selectedValue: [number, number | null]) => void
) {
  const intl = useIntl();
  const { sendEvent } = useAnalytics();
  const {
    minValue,
    maxValue,
    maxPossibleValue,
    step: rangeStep,
  } = pricePerPersonFilterRangeValues(intl.defaultCurrency);

  const ensuredSelectedValue = React.useMemo(
    () => selectedValue ?? ([minValue, maxPossibleValue] as [number, number | null]),
    [selectedValue, minValue, maxPossibleValue]
  );

  const [lowestSelectedValue, highestSelectedValue] = ensuredSelectedValue;
  const selectedRange = [lowestSelectedValue, highestSelectedValue === null ? maxPossibleValue : highestSelectedValue];

  const [currentSelectedRange, setCurrentSelectedRange] = React.useState(selectedRange);

  useUpdateEffect(() => {
    setCurrentSelectedRange(selectedRange);
  }, [ensuredSelectedValue]);

  const formattedRange = React.useMemo(() => {
    const [lowerValue, higherValue] = currentSelectedRange;

    if (higherValue > maxValue)
      return `${intl.formatTruncatedCurrency(lowerValue)} - +${intl.formatTruncatedCurrency(maxValue)}`;

    return `${intl.formatTruncatedCurrency(lowerValue)} - ${intl.formatTruncatedCurrency(higherValue)}`;
  }, [currentSelectedRange, maxValue, intl]);

  const handleValueCommit = ([lowerValue, higherValue]: number[]) => {
    setSelection([lowerValue, higherValue > maxValue ? null : higherValue]);
    sendEvent(
      createSearchFilterClickEvent({
        element: 'slider',
        type: 'price',
        result: formattedRange.replace('.', '').replace(/\s/g, '').replace('-', ' '),
      })
    );
  };

  const handleValueChange = (newSelectedRange: number[]) => {
    setCurrentSelectedRange(newSelectedRange);
  };

  return {
    labelProps: {
      formattedRange,
    },
    sliderProps: {
      step: rangeStep,
      min: minValue,
      max: maxPossibleValue,
      value: currentSelectedRange,
      onValueCommit: handleValueCommit,
      onValueChange: handleValueChange,
    },
  };
}

export function PricePerPersonFilter() {
  const intl = useIntl();

  const { isFetching, selectedValue, setSelection } = useValueFilter('PricePerPerson');

  const { labelProps, sliderProps } = useControlledSliderWithOpenRange(selectedValue, setSelection);

  return (
    <FilterGroup title={intl.formatMessage({ id: 'accommodation_price_label' })} defaultOpen>
      <RangeLabel {...labelProps} disabled={isFetching} />
      <Slider {...{ ...sliderProps, withoutIcon: true }} ariaLabel="no-label" disabled={isFetching} />
    </FilterGroup>
  );
}
